import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSession } from '../../hooks/useSession';
import { 
  ShoppingBag, 
  ArrowLeft,
} from 'lucide-react';
import toast from 'react-hot-toast';
import BookingForm from './components/BookingForm';
import OrderSummary from './components/OrderSummary';
import './Booking.css';

const Booking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useSession();
  const product = location.state?.product;

  const [formData, setFormData] = useState({
    quantity: 1,
    deliveryAddress: '',
    deliveryDate: '',
    specialInstructions: '',
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!product) {
    return (
      <motion.div 
        className="booking-page"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="container-custom">
          <motion.div 
            className="no-product"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            <motion.div
              animate={{ 
                rotate: [0, -10, 10, -10, 0],
                scale: [1, 1.1, 1]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <span className="no-product-icon">📦</span>
            </motion.div>
            <h2>No Product Selected</h2>
            <p>Please select a product to book</p>
            <motion.button 
              onClick={() => navigate('/products')} 
              className="btn-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ArrowLeft size={18} />
              Browse Products
            </motion.button>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  if (!isAuthenticated) {
    return (
      <motion.div 
        className="booking-page"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="container-custom">
          <motion.div 
            className="login-required"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <span className="login-icon">🔒</span>
            </motion.div>
            <h2>Login Required</h2>
            <p>Please login to complete your booking</p>
            <div className="auth-actions">
              <motion.button 
                onClick={() => navigate('/login')} 
                className="btn-primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Login
              </motion.button>
              <motion.button 
                onClick={() => navigate('/register')} 
                className="btn-secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Create Account
              </motion.button>
            </div>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Mock booking API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success('🎉 Booking successful! We will contact you shortly.');
      navigate('/profile');
    } catch (error) {
      toast.error('Booking failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  return (
    <motion.div 
      className="booking-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4 }}
    >
      <div className="container-custom">
        <div className="booking-header">
          <motion.button
            className="back-btn"
            onClick={() => navigate(-1)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft size={20} />
            Back
          </motion.button>
          <motion.h1 
            className="booking-title"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <ShoppingBag className="title-icon" />
            Book Your Bullion
          </motion.h1>
        </div>
        
        <div className="booking-grid">
          <BookingForm
            product={product}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            loading={loading}
          />
          <OrderSummary product={product} quantity={formData.quantity} />
        </div>
      </div>
    </motion.div>
  );
};

export default Booking;